import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import App from "./app/App";
import store from "./store/store";
import ErrorBoundary from "./app/boundaries/ErrorBoundary";
import NetworkBoundary from "./app/boundaries/NetworkBoundary";
import MaintenanceBoundary from "./app/boundaries/MaintenanceBoundary";
import SocketProvider from "app/providers/SocketProvider";
import SentryProvider from "app/providers/SentryProvider";
import SentryErrorBoundary from "app/boundaries/SentryErrorBoundary";
import GTMProvider from "app/providers/GTMProvider";
import {
  triggerErrorBoundary,
  getGlobalErrorBoundaryRef,
} from "./app/boundaries/errorBoundaryGlobal";

// Set up early error handlers (before React renders) to catch initialization errors
if (typeof window !== "undefined") {
  const resizeObserverLoopErrRe =
    /^ResizeObserver loop completed with undelivered notifications\.$/;

  // Handle synchronous errors that occur before React ErrorBoundary mounts
  window.addEventListener(
    "error",
    (event) => {
      // Ignore ResizeObserver errors
      if (resizeObserverLoopErrRe.test(event.message)) {
        event.stopImmediatePropagation();
        return;
      }

      // Ignore errors from scripts, stylesheets, images, etc. (only catch JS errors)
      if (event.target && event.target !== window && event.target.nodeName) {
        return;
      }

      // If ErrorBoundary is ready, let it handle the error
      // Otherwise, prevent default and store for later
      if (!getGlobalErrorBoundaryRef()) {
        event.preventDefault();
        const error = event.error || new Error(event.message || "Unknown error");
        if (event.filename) error.fileName = event.filename;
        if (event.lineno) error.lineNumber = event.lineno;
        if (event.colno) error.columnNumber = event.colno;
        triggerErrorBoundary(error, {
          componentStack: `Error at ${event.filename || "unknown"}:${event.lineno || "?"}:${event.colno || "?"}`,
          type: "early-synchronous-error",
        });
      }
    },
    true // Use capture phase to catch errors early
  );

  // Handle unhandled promise rejections that occur before React ErrorBoundary mounts
  window.addEventListener("unhandledrejection", (event) => {
    if (!getGlobalErrorBoundaryRef()) {
      event.preventDefault();
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason || "Unhandled promise rejection"));
      triggerErrorBoundary(error, {
        componentStack: "Early Unhandled Promise Rejection",
        type: "early-promise-rejection",
      });
    }
  });
}
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <MaintenanceBoundary>
    <NetworkBoundary>
      <ErrorBoundary>
        <SentryProvider>
          <SentryErrorBoundary boundaryName="root">
            <GTMProvider>
              <Provider store={store}>
                <SocketProvider>
                  <App />
                </SocketProvider>
              </Provider>
            </GTMProvider>
          </SentryErrorBoundary>
        </SentryProvider>
      </ErrorBoundary>
    </NetworkBoundary>
  </MaintenanceBoundary>
);
